import { createContext, useMemo, useContext, useRef } from 'react';
import { getUserFullName } from '../shared/Utils';
import React from 'react';
import { UserRole } from '../../dtos/UserRole';
import { ExternalUserRole } from '../../models/ExternalUserRole';
import LoadPanel from 'devextreme-react/load-panel';

export interface IAppContext {
    loginUser: any,
    userEmailAddress: string,
    userId: string,
    userName: string,
    userRole: number, //TODO update to UserRole type
    userProfiles: number[],
    userPermissions: IUserPermissions,
    externalUserRoles: any, //TODO update to defined type
    configuration: any, //TODO update to defined type
    loggedIn: boolean,
    UserIsGMS: boolean,
    UserIsORF: boolean,
    UserIsPI: boolean,
    UserIsPO: boolean,
    UserIsSO: boolean,
    UserIsSuper: boolean,
    UserIsExternalUser: boolean,
    UserIsPM: boolean,
    errorToast: (message: string) => void,
    infoToast: (message: string) => void,
    showLoadPanel: (message?: string) => void,
    hideLoadPanel: () => void,
    // True while the global load panel is showing. Scoped tasklist spinners check this so they don't
    // stack a second overlay on top of it (which reads as competing/flashing indicators).
    isLoadPanelVisible?: () => boolean
}

interface IAppContextProvider {
    children: any,
    userInfo: any,
    configuration: any,
    loggedIn: boolean,
    errorToast: (message: string) => void,
    infoToast: (message: string) => void
}

export interface IUserPermissions {
    viewAccess: any[],
    externalUserAccess: any[],
    allowChangeTitleInstitution: boolean,
    allowManageExternalUsers: boolean,
    allowConfiguration: boolean,
    allDashboardAccess: boolean,
    allPortfolioAccess: boolean,
    allowDeleteAll: boolean
}

export const AppContext = createContext<IAppContext | undefined>(undefined);

export const useAppContext = () => {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error('useAppContext must be used within a AppContextProvider');
    }
    return context;
};

export function AppProvider({ children, userInfo, configuration, loggedIn, errorToast, infoToast }: IAppContextProvider) {

    const loadPanel = useRef<LoadPanel>(null);

    const showLoadPanel = (message?: string) => {
        if (message)
            loadPanel.current?.instance?.option("message", message);
        else
            loadPanel.current?.instance?.option("message", "Loading...");
        loadPanel.current?.instance?.show();
    }

    const hideLoadPanel = () => {
        loadPanel.current?.instance?.hide();
    }

    // Reads the panel's actual visibility (not a counter), so an unbalanced show/hide can't leave it
    // stuck reporting visible.
    const isLoadPanelVisible = () => !!loadPanel.current?.instance?.option("visible");

    const value = useMemo(() => {

        var loginUser = getUserFullName(userInfo);
        var userName = (userInfo?.userName != null) ? userInfo.userName : userInfo?.email;

        var userRole = userInfo?.role;

        return {
            loginUser: loginUser,
            userEmailAddress: userInfo?.email,
            userId: userInfo?.userId,
            userName: userName,
            userRole: userRole,
            userProfiles: userInfo?.userProfiles,
            userPermissions: userInfo?.permissions,
            externalUserRoles: userInfo?.externalUserRoles,
            configuration: configuration,
            loggedIn: loggedIn,
            UserIsGMS: userRole === UserRole.GMS,
            UserIsORF: userRole === UserRole.ORF_DTR || userRole === UserRole.ORF_DEP,
            UserIsPI: userRole === UserRole.PI,
            UserIsPO: userRole === UserRole.PO,
            UserIsSO: userRole === UserRole.SO,
            UserIsSuper: userRole === UserRole.SU,
            UserIsExternalUser: userRole === UserRole.EXTERNAL_USER,
            // PM is an external user with PROJECT_MANAGER in their externalUserAccess permissions
            // Only PMs have externalUserAccess that includes PROJECT_MANAGER
            UserIsPM: userRole === UserRole.EXTERNAL_PROJECT_MANAGER ||
                (userRole === UserRole.EXTERNAL_USER && userInfo?.externalUserRoles &&
                    Object.values(userInfo.externalUserRoles).some((grantRoles: any) =>
                        Array.isArray(grantRoles) && grantRoles.some((permissions: any) =>
                            permissions.externalUserAccess?.includes(ExternalUserRole.PROJECT_MANAGER)
                        )
                    )
                ),
            errorToast: errorToast,
            infoToast: infoToast,
            showLoadPanel: showLoadPanel,
            hideLoadPanel: hideLoadPanel,
            isLoadPanelVisible: isLoadPanelVisible
        };
    }, [userInfo, loggedIn]);

    return (
        <AppContext.Provider value={value}>
            {children}
            <LoadPanel
                hideOnOutsideClick={false}
                ref={loadPanel}
                shading={true}
                shadingColor="rgba(0,0,0,0.4)"
                showIndicator={true}
                showPane={true}
            />
        </AppContext.Provider>
    );
};
